import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateEmail } from '../utils/validation';
import { sanitizeReturnUrl } from '../utils/urlUtils';
import './GuestEmailForm.css';

interface GuestEmailFormProps {
  returnUrl: string;
}

/**
 * Guest Email Form Component
 * Allows users to continue as a guest by providing their email address.
 * Redirects to the return URL with guestEmail query parameter.
 */
export const GuestEmailForm: React.FC<GuestEmailFormProps> = ({ returnUrl }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState<{ key: string; params?: Record<string, string | number> } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const result = validateEmail(email);
    if (!result.isValid) {
      setValidationError({ key: result.errorKey ?? 'validation.fallback.invalidEmail', params: result.params });
      return;
    }

    setIsSubmitting(true);

    try {
      const sanitized = sanitizeReturnUrl(returnUrl);
      const url = new URL(sanitized);
      url.searchParams.set('guestEmail', email);
      window.location.href = url.toString();
    } catch {
      setValidationError({ key: 'guest.redirectError' });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="guest-email-form">
      <h2 className="guest-email-form__title">{t('guest.heading')}</h2>
      <form onSubmit={handleSubmit} noValidate>
        <div className="guest-email-form__field">
          <label htmlFor="guest-email" className="guest-email-form__label">
            {t('guest.emailLabel')}
          </label>
          <input
            type="email"
            id="guest-email"
            name="guestEmail"
            value={email}
            onChange={handleEmailChange}
            className={`guest-email-form__input ${validationError ? 'guest-email-form__input--error' : ''}`}
            placeholder={t('guest.emailPlaceholder')}
            required
            autoComplete="email"
            aria-describedby={validationError ? 'guest-email-error' : undefined}
            disabled={isSubmitting}
          />
          {validationError && (
            <div id="guest-email-error" className="guest-email-form__field-error" role="alert">
              {t(validationError.key, validationError.params)}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="guest-email-form__submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('guest.submitting') : t('guest.submit')}
        </button>
      </form>
    </div>
  );
};
